import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown, Search, MessageCircle, BadgeEuro, ShieldCheck, Undo2, Receipt } from "lucide-react";
import { api, downloadPdf, errMsg, fmtDate } from "@/lib/api";
import { ddtClientWa } from "@/lib/ddt";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, Empty, PageHead, Pill } from "@/components/common";
import { PayDialog } from "@/components/admin/PayDialog";

export default function DdtArchive({ asl = false }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [pay, setPay] = useState(null);
  const load = useCallback(() => { api.get("/admin/ddts", { params: { asl } }).then((r) => { setItems(r.data); setSel({}); }); }, [asl]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => items.filter((d) => !q || [d.numero, d.numero_asl, d.speditore_nome, d.origine_nome, d.cliente_nome, d.vet_name, ...(d.righe || []).map((r) => r.microchip)]
    .some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()))), [items, q]);
  const selected = Object.keys(sel).filter((k) => sel[k]);

  const del = async (d) => { if (window.confirm(`Eliminare il DDT n. ${d.numero}?`)) { await api.delete(`/admin/ddts/${d.id}`); load(); } };
  const pdf = async (d) => { try { await downloadPdf(`/ddts/${d.id}/pdf`, `DDT_${(asl ? d.numero_asl : d.numero).replace("/", "-")}.pdf`); } catch (e) { toast.error(errMsg(e)); } };
  const fattura = async (d) => {
    try {
      const { data } = await api.post(`/admin/fatture/from-ddt/${d.id}`);
      toast.success(`Fattura n. ${data.numero} ${d.fattura_numero ? "aperta" : "creata"}`);
      await downloadPdf(`/admin/fatture/${data.id}/pdf`, `Fattura_${data.numero.replace("/", "-")}.pdf`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const moveAsl = async () => {
    try {
      if (asl) { await api.post("/admin/ddts/asl/remove", { ids: selected }); toast.success("Copie ASL eliminate; gli originali restano in archivio"); }
      else { const { data } = await api.post("/admin/ddts/asl", { ids: selected }); toast.success(`${data.moved.length} DDT copiati in DDT ASL con nuova numerazione (gli originali restano qui)`); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={asl ? "admin-ddt-asl" : "admin-ddt-archive"}>
      <PageHead eyebrow={asl ? "DDT ASL" : "Archivio DDT"} title={asl ? "Documenti per ASL" : "Documenti di trasporto"}
        subtitle={asl ? "Numerazione ASL separata e progressiva. Il numero originale resta consultabile." : "Documento commerciale semplificato SOA – Categoria 1 – Reg. CE 1069/2009. Seleziona uno o più DDT per spostarli nella sezione ASL."}>
        {selected.length > 0 && (
          <Button variant="outline" onClick={moveAsl} data-testid="ddt-move-asl" className="rounded-full">
            {asl ? <><Undo2 className="h-4 w-4 mr-1" />Elimina copia ASL ({selected.length})</> : <><ShieldCheck className="h-4 w-4 mr-1" />Copia in DDT ASL ({selected.length})</>}
          </Button>
        )}
        {!asl && <Link to="/admin/ddt/nuovo" data-testid="ddt-new-button" className="inline-flex items-center rounded-full bg-[#0D3B52] hover:bg-[#082535] text-white px-5 h-10 text-sm font-medium"><Plus className="h-4 w-4 mr-1" />Nuovo DDT</Link>}
      </PageHead>
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3 top-3 text-[#5C6B73]" />
        <Input placeholder="Cerca numero, speditore, cliente, microchip…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="ddt-search" />
      </div>
      {rows.length === 0 ? <Empty>Nessun documento in questa sezione.</Empty> : (
        <Card className="overflow-x-auto">
          <Table data-testid="ddt-table">
            <TableHeader><TableRow>
              <TableHead className="w-8"><Checkbox checked={rows.length > 0 && selected.length === rows.length} onCheckedChange={(v) => setSel(v ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} data-testid="ddt-select-all" /></TableHead>
              <TableHead>N°</TableHead><TableHead>Data</TableHead><TableHead>Speditore</TableHead><TableHead>Merce</TableHead><TableHead>Cliente / Vet</TableHead><TableHead>Pagamento</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} data-testid={`ddt-row-${d.id}`}>
                  <TableCell><Checkbox checked={!!sel[d.id]} onCheckedChange={(v) => setSel((p) => ({ ...p, [d.id]: !!v }))} data-testid={`ddt-select-${d.id}`} /></TableCell>
                  <TableCell className="font-display text-lg font-semibold text-[#0D3B52]">
                    {asl ? d.numero_asl : d.numero}
                    {asl && <span className="block text-[11px] text-[#5C6B73] font-sans">orig. {d.numero}</span>}
                    {!asl && d.asl_copy && <span className="block text-[11px] text-[#997316] font-sans">copia in DDT ASL</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(d.data)}</TableCell>
                  <TableCell className="text-sm">{d.speditore_nome}<span className="block text-xs text-[#5C6B73]">{d.origine_indirizzo}</span></TableCell>
                  <TableCell className="text-sm">{(d.righe || []).map((r) => r.specie).filter(Boolean).join(", ")}</TableCell>
                  <TableCell className="text-sm">{d.cliente_nome}{d.vet_name ? <span className="block text-xs text-[#5C6B73]">{d.vet_name}</span> : null}</TableCell>
                  <TableCell>
                    <button type="button" onClick={() => setPay(d)} data-testid={`ddt-pay-${d.id}`} className="text-left">
                      <Pill cls={d.pagato ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"}>
                        <BadgeEuro className="h-3 w-3 mr-1" />{d.pagato ? `Pagato ${fmtDate(d.pagato_data)}` : "Segna pagato"}
                      </Pill>
                    </button>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="icon" variant="ghost" onClick={() => pdf(d)} data-testid={`ddt-pdf-${d.id}`}><FileDown className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => fattura(d)} data-testid={`ddt-fattura-${d.id}`} className="text-[#997316]"><Receipt className="h-4 w-4 mr-1" />{d.fattura_numero ? `Fatt. ${d.fattura_numero}` : "Fattura"}</Button>
                    {d.cliente_telefono && <a href={ddtClientWa(d)} target="_blank" rel="noopener noreferrer" data-testid={`ddt-wa-${d.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#1DA851] hover:bg-emerald-50"><MessageCircle className="h-4 w-4" /></a>}
                    <Link to={`/admin/ddt/${d.id}`} data-testid={`ddt-edit-${d.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-[#F3EFEA]"><Pencil className="h-4 w-4" /></Link>
                    <Button size="icon" variant="ghost" onClick={() => del(d)} data-testid={`ddt-delete-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <PayDialog ddt={pay} open={!!pay} onOpenChange={(o) => !o && setPay(null)} onSaved={load} />
    </div>
  );
}
